import PaymentFinancialSummary from '@/src/Activations/components/PaymentFinancialSummary';
import PaymentRecordsTable from '@/src/Activations/components/PaymentRecordsTable';
import PaymentConfiguration from '@/src/Activations/components/PaymentConfiguration';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment-timezone';
import useActivationPaymentSettings, {
    ActivationPaymentSettings,
    PaymentRecord,
    PaymentReportData,
} from '@/src/Activations/components/hooks/useActivationPaymentSettings';

/*
 * Payments — paid photo sessions for this event (ADMIN-MERGE Phase C;
 * classic's #payments tab). Stripe keys are write-only; the report
 * half renders on house primitives instead of classic's hand-rolled
 * div charts. The server gates writes and reports to admins — the
 * page shows an honest access note on 401.
 */

const RANGES = [
    { key: '7', label: '7 days' },
    { key: '30', label: '30 days' },
    { key: '90', label: '90 days' },
] as const;




const dollars = (cents: number) =>
    (cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
    });

function Tile({
    label,
    value,
    sub,
}: {
    label: string;
    value: string;
    sub?: React.ReactNode;
}) {
    return (
        <div>
            <div className="text-[21px] font-bold leading-none tracking-[-0.01em] tabular-nums">
                {value}
            </div>
            <div className="mt-1.5 font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-faint">
                {label}
            </div>
            {sub && <div className="mt-1 text-[11px]">{sub}</div>}
        </div>
    );
}

export default function PaymentsV2() {
    const { activationId } = useParams();
    const id = activationId ? +activationId : 0;
    const navigate = useNavigate();
    const {
        getActivationPaymentSettings,
        getPaymentReportData,
        getPaymentRecords,
    } = useActivationPaymentSettings();

    const [settings, setSettings] =
        useState<ActivationPaymentSettings | null>(null);
    const [denied, setDenied] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [range, setRange] = useState<string>('30');
    const [custom, setCustom] = useState({ start: '', end: '' });
    const [report, setReport] = useState<PaymentReportData | null>(null);
    const [records, setRecords] = useState<PaymentRecord[]>([]);


    const rangeDates = (): { start?: string; end?: string } => {
        if (range === 'custom') {
            return {
                start: custom.start || undefined,
                end: custom.end || undefined,
            };
        }
        return {
            start: moment()
                .subtract(+range, 'days')
                .format('YYYY-MM-DD'),
            end: moment().format('YYYY-MM-DD'),
        };
    };

    useEffect(() => {
        let mounted = true;
        getActivationPaymentSettings(id)
            .then((data) => {
                if (!mounted) return;
                setSettings(data);
                setIsLoading(false);
            })
            .catch(() => {
                if (mounted) {
                    setDenied(true);
                    setIsLoading(false);
                }
            });
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!settings) return;
        if (range === 'custom' && (!custom.start || !custom.end)) return;
        let mounted = true;
        const { start, end } = rangeDates();
        Promise.all([
            getPaymentReportData(id, start, end).catch(() => null),
            getPaymentRecords(id, start, end).catch(() => []),
        ]).then(([reportData, recordRows]) => {
            if (!mounted) return;
            setReport(reportData);
            setRecords(recordRows ?? []);

        });
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings?.id, range, custom.start, custom.end]);

    if (isLoading) {
        return (
            <main className="mx-auto max-w-[920px] px-[clamp(18px,3vw,34px)] pt-4">
                {[0, 1].map((i) => (
                    <div
                        key={i}
                        className="mb-4 h-20 animate-pulse rounded-[12px] bg-secondary"
                    />
                ))}
            </main>
        );
    }

    if (denied) {
        return (
            <main className="mx-auto max-w-[920px] px-[clamp(18px,3vw,34px)] pt-4">
                <div className="ps-label mb-2">Payments</div>
                <p className="text-[13px] text-muted-foreground">
                    Payments needs an admin account — ask an org admin for
                    access.
                </p>
            </main>
        );
    }

    const maxRevenue = Math.max(
        1,
        ...(report?.dailyChartData ?? []).map((d) => d.revenue),
    );
    const weekDelta =
        report && report.paymentsPreviousWeek > 0
            ? Math.round(
                  ((report.paymentsCurrentWeek -
                      report.paymentsPreviousWeek) /
                      report.paymentsPreviousWeek) *
                      100,
              )
            : null;

    return (
        <main className="mx-auto max-w-[920px] px-[clamp(18px,3vw,34px)] pt-4">
            <div className="ps-label mb-4">
                <button
                    type="button"
                    onClick={() => navigate(`/v2/activations/${id}`)}
                    className="ps-label cursor-pointer border-0 bg-transparent p-0 hover:text-foreground"
                >
                    Overview
                </button>
                <span className="mx-1.5 opacity-60">/</span>
                <span className="text-muted-foreground">Payments</span>
            </div>

            <PaymentConfiguration activationId={id} onSaved={setSettings} />

            {settings && (
                <>
                    <div className="mb-4 flex flex-wrap items-center gap-1.5">
                        {RANGES.map((r) => (
                            <button
                                key={r.key}
                                type="button"
                                onClick={() => setRange(r.key)}
                                className={
                                    'cursor-pointer rounded-full px-3 py-[5px] font-[inherit] text-[12px] font-semibold ' +
                                    (range === r.key
                                        ? 'border border-transparent bg-brand-soft text-brand-ink'
                                        : 'border border-border bg-transparent text-muted-foreground')
                                }
                            >
                                {r.label}
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={() => setRange('custom')}
                            className={
                                'cursor-pointer rounded-full px-3 py-[5px] font-[inherit] text-[12px] font-semibold ' +
                                (range === 'custom'
                                    ? 'border border-transparent bg-brand-soft text-brand-ink'
                                    : 'border border-border bg-transparent text-muted-foreground')
                            }
                        >
                            Custom
                        </button>
                        {range === 'custom' && (
                            <span className="flex items-center gap-1.5">
                                <input
                                    type="date"
                                    className="ps-input text-[12px]"
                                    value={custom.start}
                                    onChange={(e) =>
                                        setCustom((c) => ({
                                            ...c,
                                            start: e.target.value,
                                        }))
                                    }
                                />
                                <span className="text-faint">–</span>
                                <input
                                    type="date"
                                    className="ps-input text-[12px]"
                                    value={custom.end}
                                    onChange={(e) =>
                                        setCustom((c) => ({
                                            ...c,
                                            end: e.target.value,
                                        }))
                                    }
                                />
                            </span>
                        )}
                    </div>

                    {report && (
                        <>
                            <div className="mb-8 flex flex-wrap gap-9">
                                <Tile
                                    label="Last 24h"
                                    value={dollars(report.revenueLast24h)}
                                    sub={
                                        <span className="text-faint">
                                            {report.paymentsLast24h} payment
                                            {report.paymentsLast24h === 1
                                                ? ''
                                                : 's'}
                                        </span>
                                    }
                                />
                                <Tile
                                    label="This week"
                                    value={dollars(
                                        report.revenueCurrentWeek,
                                    )}
                                    sub={
                                        weekDelta != null ? (
                                            <span
                                                className={
                                                    weekDelta >= 0
                                                        ? 'font-semibold text-good'
                                                        : 'font-semibold text-attention'
                                                }
                                            >
                                                {weekDelta >= 0 ? '+' : ''}
                                                {weekDelta}% wow
                                            </span>
                                        ) : undefined
                                    }
                                />
                                <Tile
                                    label="This month"
                                    value={dollars(
                                        report.revenueCurrentMonth,
                                    )}
                                />
                                <Tile
                                    label="Lifetime"
                                    value={dollars(report.revenueLifetime)}
                                    sub={
                                        <span className="text-faint">
                                            {report.paymentsLifetime.toLocaleString()}{' '}
                                            payments
                                        </span>
                                    }
                                />
                                <Tile
                                    label="Sessions"
                                    value={report.totalSessions.toLocaleString()}
                                    sub={
                                        <span className="text-faint">
                                            {report.totalClaimed} claimed ·{' '}
                                            {report.totalInvalidated}{' '}
                                            invalidated
                                        </span>
                                    }
                                />
                            </div>

                            {report.dailyChartData.length > 1 && (
                                <div className="mb-8">
                                    <div className="ps-label mb-2">
                                        Revenue by day
                                    </div>
                                    <div className="flex h-[72px] items-end gap-[3px]">
                                        {report.dailyChartData.map(
                                            (day) => (
                                                <div
                                                    key={day.date}
                                                    title={`${moment(
                                                        day.date,
                                                    ).format(
                                                        'MMM D',
                                                    )} · ${dollars(
                                                        day.revenue,
                                                    )} · ${
                                                        day.count
                                                    } payments`}
                                                    className="min-w-[4px] flex-1 rounded-t-[3px] bg-brand"
                                                    style={{
                                                        height: `${Math.max(
                                                            3,
                                                            (day.revenue /
                                                                maxRevenue) *
                                                                100,
                                                        )}%`,
                                                        opacity:
                                                            day.revenue > 0
                                                                ? 1
                                                                : 0.25,
                                                    }}
                                                />
                                            ),
                                        )}
                                    </div>
                                    <div className="mt-1 flex justify-between font-mono text-[9px] uppercase tracking-[0.1em] text-faint">
                                        <span>
                                            {moment(
                                                report.dailyChartData[0]
                                                    .date,
                                            ).format('MMM D')}
                                        </span>
                                        <span>
                                            {moment(
                                                report.dailyChartData[
                                                    report.dailyChartData
                                                        .length - 1
                                                ].date,
                                            ).format('MMM D')}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {report && <PaymentFinancialSummary report={report} />}
                    <PaymentRecordsTable paymentRecords={records} />
                </>
            )}
        </main>
    );
}
